import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation, useSearchParams } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import Footer from '../Common/Footer';
import LanguageDrawer from '../Common/Drawers/LanguageDrawer';
import backendClient from '../../services/backendClient';
import { getCustomerPortalUrl, checkSubscriptionStatus, getSafeStripeReturnPath } from '../../services/stripeService';
import { useStripePrices } from '../../services/priceConfig';
import { getCurrentLanguage, supportedLanguages, changeLanguage } from '../../services/i18n';

// Reusable Input Component with states
const ProfileInput = ({ icon, label, value, onChange, type = 'text', disabled = false, error = '', onBlur }) => {
  const [isFocused, setIsFocused] = useState(false);
  const hasValue = value && value.length > 0;
  const isActive = isFocused || hasValue; // Show floating label on focus OR when has value
  const hasError = error && error.length > 0;

  const handleBlur = () => {
    setIsFocused(false);
    if (onBlur) onBlur();
  };
  
  return (
    <div className="w-full">
      <div 
        className={`
          h-[56px] rounded-[12px] relative transition-all duration-200
          ${disabled 
            ? 'bg-[#EBEBEA]' 
            : isActive 
              ? 'bg-white' 
              : 'bg-[#f8f8f7] md:hover:bg-white'
          }
        `}
      >
        {/* Border overlay */}
        <div 
          aria-hidden="true" 
          className={`absolute inset-0 pointer-events-none rounded-[12px] transition-all duration-200 ${
            hasError 
              ? 'border-[1.5px] border-[#d40b0b]' 
              : isFocused 
                ? 'border-[1.5px] border-[#0d0c07]' 
                : 'border border-[#56554d]/50'
          }`}
        />
        
        <div className="flex items-center h-full px-3 gap-2">
          {/* Icon */}
          <div className="flex-shrink-0 w-6 h-6 flex items-center justify-center">
            {icon}
          </div>
          
          {/* Input area */}
          <div className="flex-1 relative h-full flex flex-col justify-center">
            {/* Floating label - single label that moves between positions */}
            <span 
              className={`
                absolute left-0 pointer-events-none
                transition-[top,font-size,line-height,color] duration-200
                ${hasError ? 'text-[#d40b0b]' : 'text-[#72726e]'}
                ${isActive 
                  ? 'top-[8px] text-[10.8px] leading-[14px]' 
                  : 'top-[16px] text-[16px] leading-[24px]'
                }
              `}
            >
              {label}
            </span>
            
            {disabled ? (
              <p className={`font-semibold text-[16px] text-[#0d0c07] transition-all duration-200 ${hasValue ? 'pt-[14px]' : ''}`}>
                {value || ''}
              </p>
            ) : (
              <input
                type={type}
                value={value || ''}
                onChange={onChange}
                onFocus={() => setIsFocused(true)}
                onBlur={handleBlur}
                className={`
                  w-full bg-transparent text-[16px] text-[#0d0c07]
                  outline-none border-none focus:outline-none focus:ring-0
                  leading-[24px] transition-all duration-200
                  ${isActive ? 'pt-[14px] font-semibold' : 'font-normal pt-0'}
                `}
                style={{ boxShadow: 'none' }}
              />
            )}
          </div>
        </div>
      </div>
      
      {/* Error message */}
      {hasError && (
        <p className="mt-1 text-[12px] text-[#d40b0b] leading-[16px]">
          {error}
        </p>
      )}
    </div>
  );
};

// Update Notification Component
const UpdateNotification = ({ isVisible, message = "Your profile details were updated." }) => {
  const [shouldRender, setShouldRender] = useState(false);
  const [isAnimatingIn, setIsAnimatingIn] = useState(false);

  useEffect(() => {
    if (isVisible) {
      setShouldRender(true);
      // Small delay to ensure the element is rendered before animating
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsAnimatingIn(true);
        });
      });
    } else {
      setIsAnimatingIn(false);
      // Wait for animation to complete before unmounting
      const timer = setTimeout(() => {
        setShouldRender(false);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [isVisible]);

  if (!shouldRender) return null;

  return (
    <div 
      className={`
        fixed z-[100] transition-transform duration-300 ease-out
        left-6 right-6 bottom-6
        md:left-8 md:right-auto md:bottom-8 md:w-auto md:min-w-[320px] md:max-w-[400px]
        ${isAnimatingIn ? 'translate-y-0' : 'translate-y-[calc(100%+32px)]'}
      `}
    >
      <div className="bg-white relative rounded-[12px] w-full">
        <div className="w-full">
          <div className="box-border flex gap-[20px] items-start overflow-clip p-[12px] relative w-full">
            
            {/* Wrapper */}
            <div className="flex-1 flex gap-[12px] items-start min-w-0">
              
              {/* Check */}
              <div className="bg-[#67b204] flex gap-[10px] items-center p-[2px] rounded-[6px] shrink-0">
                <div className="shrink-0 w-[24px] h-[24px]">
                  <svg className="block w-full h-full" fill="none" viewBox="0 0 24 24">
                    <path d="M20 6L9 17L4 12" stroke="white" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.75"/>
                  </svg>
                </div>
              </div>
              
              {/* Info */}
              <div className="flex-1 flex flex-col items-start min-w-0">
                <div className="flex gap-[10px] items-start w-full">
                  <p className="flex-1 font-bold leading-[28px] min-w-0 text-[#0d0c07] text-[15.2px]">{message}</p>
                </div>
              </div>
              
            </div>
            
          </div>
        </div>
        <div 
          aria-hidden="true" 
          className="absolute border-[#e2e1df] border-[0.5px] border-solid inset-[-0.5px] pointer-events-none rounded-[12.5px]"
          style={{ boxShadow: '0px 0px 1px 0px rgba(13,12,7,0.12), 0px -4px 24px 0px rgba(13,12,7,0.12), 1px -2px 8px 0px rgba(13,12,7,0.08)' }}
        />
      </div>
    </div>
  );
};

function MyProfile() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation('common');
  const [token] = useState(localStorage.getItem('authToken'));
  const [user, setUser] = useState({ name: '', email: '', phone: '' });
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [phoneError, setPhoneError] = useState('');
  const [showNotification, setShowNotification] = useState(false);
  const [isPortalLoading, setIsPortalLoading] = useState(false);
  const [cancelAtPeriodEnd, setCancelAtPeriodEnd] = useState(false);
  const [isProfileLoaded, setIsProfileLoaded] = useState(false);
  const prices = useStripePrices();
  const [showLanguageDrawer, setShowLanguageDrawer] = useState(false);
  const [currentLanguage, setCurrentLanguage] = useState(getCurrentLanguage());
  const returnToPath = getSafeStripeReturnPath(searchParams.get('returnTo'));

  const currentLanguageInfo = supportedLanguages.find(lang => lang.code === currentLanguage) || supportedLanguages[0];

  const handleLanguageChange = async (languageCode) => {
    changeLanguage(languageCode);
    setCurrentLanguage(languageCode);
    window.dispatchEvent(new CustomEvent('languageChanged', { detail: languageCode }));
    const authToken = localStorage.getItem('authToken');
    if (authToken) {
      try {
        await backendClient.put('/api/user/language', { language: languageCode });
      } catch (error) {
        console.error('Error saving language preference:', error);
      }
    }
  };

  // Validate phone number - only numbers allowed, empty is acceptable
  const validatePhone = () => {
    if (!user.phone || user.phone.length === 0) {
      setPhoneError('');
      return true;
    }
    
    const numbersOnly = /^[0-9]+$/;
    if (!numbersOnly.test(user.phone)) {
      setPhoneError('Phone number can only contain numbers');
      return false;
    }
    
    setPhoneError('');
    return true;
  };

  // Fetch user data + sync premium status from Stripe
  const fetchProfile = React.useCallback(async () => {
    if (!token) return;
    try {
      const data = await backendClient.get('/api/user/me');
      if (data && data.email) {
        setUser(data);
        // If premium, sync latest subscription data from Stripe
        if (data.isPremium) {
          try {
            const status = await checkSubscriptionStatus();
            const sub = status?.subscription;
            const periodEnd = sub?.current_period_end 
              || sub?.items?.data?.[0]?.current_period_end;
            if (status?.isPremium && periodEnd) {
              setUser(prev => ({
                ...prev,
                premiumExpiresAt: new Date(periodEnd * 1000).toISOString()
              }));
            }
            // Subscription is cancelling if cancel_at_period_end is set OR cancel_at is set
            const isCancelling = !!(sub?.cancel_at_period_end || sub?.cancel_at);
            setCancelAtPeriodEnd(isCancelling);
            // If cancel_at is set, use that as the expiry date instead of period end
            if (isCancelling && sub?.cancel_at) {
              setUser(prev => ({
                ...prev,
                premiumExpiresAt: new Date(sub.cancel_at * 1000).toISOString()
              }));
            }
          } catch (e) {
            // Non-critical — profile still shows without date
          }
        }
        setIsProfileLoaded(true);
      }
    } catch (err) {
      if (err.name === 'AuthError') {
        localStorage.removeItem('authToken');
        navigate('/');
        return;
      }
      console.error('Failed to fetch user:', err);
    }
  }, [token, navigate]);

  useEffect(() => {
    // Redirect to home if not logged in
    if (!token) {
      navigate('/');
      return;
    }

    fetchProfile();
  }, [token, navigate, fetchProfile]);

  // Re-fetch subscription status when returning from Stripe portal
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && token) {
        fetchProfile();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [token, fetchProfile]);

  const handleProfileSave = async () => {
    setIsSaving(true);
    setSaveSuccess(false);
    try {
      await backendClient.put('/api/user/me', user);
      setSaveSuccess(true);
      setShowNotification(true);
      setTimeout(() => {
        setSaveSuccess(false);
        setShowNotification(false);
      }, 3000);
    } catch (e) {
      console.error('Failed to save profile:', e);
    } finally {
      setIsSaving(false);
    }
  };

  // Icons
  const UserIcon = (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 20 20">
      <path d="M16.666 17.5V15.833a3.333 3.333 0 0 0-3.333-3.333H6.666a3.333 3.333 0 0 0-3.333 3.333V17.5M13.333 5.833a3.333 3.333 0 1 1-6.666 0 3.333 3.333 0 0 1 6.666 0Z" stroke="#0D0C07" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );

  const MailIcon = (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 20 20">
      <path d="M3.333 3.333h13.334c.916 0 1.666.75 1.666 1.667v10c0 .917-.75 1.667-1.666 1.667H3.333c-.916 0-1.666-.75-1.666-1.667V5c0-.917.75-1.667 1.666-1.667ZM3.333 5l6.667 5 6.667-5" stroke="#0D0C07" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );

  const PhoneIcon = (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 20 20">
      <path d="M5.833 2.5h8.334c.663 0 1.299.263 1.768.732.469.47.732 1.105.732 1.768v10c0 .663-.263 1.299-.732 1.768a2.5 2.5 0 0 1-1.768.732H5.833a2.5 2.5 0 0 1-1.768-.732A2.5 2.5 0 0 1 3.333 15V5c0-.663.263-1.299.732-1.768A2.5 2.5 0 0 1 5.833 2.5ZM10 14.167h.008" stroke="#0D0C07" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );

  return (
    <div className="min-h-safe-screen bg-[#fff] flex flex-col">
      {/* Content */}
      <div className="flex-1 w-full max-w-3xl mx-auto px-6 py-8">
        <div className="flex flex-col gap-5">
          
          {/* Title */}
          <h1 className="font-bold text-[18.8px] leading-[36px] text-[#0d0c07]">
            {t('profileSettings.settings')}
          </h1>

          {/* Form */}
          <div className="flex flex-col gap-6">
            
            {/* Inputs */}
            <div className="flex flex-col gap-4">
              <ProfileInput
                icon={UserIcon}
                label={t('profileSettings.name')}
                value={user.name}
                onChange={e => setUser({ ...user, name: e.target.value })}
              />
              
              <ProfileInput
                icon={MailIcon}
                label={t('profileSettings.email')}
                value={user.email}
                disabled={true}
              />
              
              <ProfileInput
                icon={PhoneIcon}
                label={t('profileSettings.phone')}
                value={user.phone}
                type="tel"
                onChange={e => setUser({ ...user, phone: e.target.value })}
                error={phoneError}
                onBlur={validatePhone}
              />

              {/* Language Selector */}
              <button
                onClick={() => setShowLanguageDrawer(true)}
                className="h-[56px] rounded-[12px] relative bg-[#f8f8f7] md:hover:bg-white transition-all duration-200 w-full text-left cursor-pointer"
              >
                <div aria-hidden="true" className="absolute inset-0 pointer-events-none rounded-[12px] border border-[#56554d]/50" />
                <div className="flex items-center h-full px-3 gap-2">
                  <div className="flex-shrink-0 w-6 h-6 flex items-center justify-center">
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 20 20">
                      <path d="M10 1.667A8.333 8.333 0 1 0 10 18.333 8.333 8.333 0 0 0 10 1.667ZM1.667 10h16.666M10 1.667A12.75 12.75 0 0 1 13.333 10 12.75 12.75 0 0 1 10 18.333 12.75 12.75 0 0 1 6.667 10 12.75 12.75 0 0 1 10 1.667Z" stroke="#0D0C07" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <div className="flex-1 relative h-full flex flex-col justify-center">
                    <span className="absolute left-0 pointer-events-none text-[#72726e] top-[8px] text-[10.8px] leading-[14px]">
                      {t('profileSettings.language')}
                    </span>
                    <p className="font-semibold text-[16px] text-[#0d0c07] pt-[14px]">
                      {currentLanguageInfo.flag} {currentLanguageInfo.name}
                    </p>
                  </div>
                  <svg className="shrink-0" width="20" height="20" fill="none" viewBox="0 0 20 20">
                    <path d="M4.167 10h11.666M10 4.167L15.833 10 10 15.833" stroke="#0D0C07" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
              </button>

            </div>

            {/* Update Button */}
            <button
              onClick={handleProfileSave}
              disabled={isSaving}
              className={`
                w-full h-[48px] rounded-[12px] font-semibold text-[14px] text-white
                transition-colors duration-200 flex items-center justify-center gap-2
                ${saveSuccess ? 'bg-[#006600]' : 'bg-[#0d0c07] md:hover:bg-[#353227]'}
                disabled:opacity-70
              `}
            >
              {isSaving ? (
                <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                </svg>
              ) : saveSuccess ? (
                <>
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"/>
                  </svg>
                  {t('profileSettings.saved')}
                </>
              ) : (
                t('profileSettings.update')
              )}
            </button>

          </div>

          {/* Premium Subscription Section */}
          {isProfileLoaded && (
          <div className="flex flex-col gap-3 mt-2">
            <h2 className="font-bold text-[18.8px] leading-[36px] text-[#0d0c07]">
              {t('profileSettings.subscription')}
            </h2>

            {user.isPremium ? (
              /* Active Premium */
              <button
                onClick={async () => {
                  setIsPortalLoading(true);
                  try {
                    const { url } = await getCustomerPortalUrl({ returnPath: returnToPath });
                    if (url) window.location.href = url;
                  } catch (err) {
                    console.error('Failed to open subscription portal:', err);
                  } finally {
                    setIsPortalLoading(false);
                  }
                }}
                disabled={isPortalLoading}
                className="bg-white relative rounded-[12px] p-5 border-[0.5px] border-[#e2e1df] w-full text-left md:hover:bg-[#f8f8f7] transition-colors disabled:opacity-70 cursor-pointer"
                style={{ boxShadow: '0px 1px 2px 0px rgba(35,33,22,0.02), 4px 12px 20px 0px rgba(83,81,69,0.12)' }}
              >
                <div className="flex items-center gap-3">
                  <div className="bg-[#090] flex items-center justify-center rounded-[8px] size-[36px] shrink-0">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                      <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" stroke="#FFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-[15.2px] text-[#0d0c07] leading-[24px]">{t('profileSettings.premiumActive')}</p>
                    {user.premiumExpiresAt && (
                      <p className="text-[13.2px] text-[#56554D] leading-[20px]">
                        {cancelAtPeriodEnd
                          ? t('profileSettings.expiresOn', { date: new Date(user.premiumExpiresAt).toLocaleDateString() })
                          : t('profileSettings.renewsOn', { date: new Date(user.premiumExpiresAt).toLocaleDateString() })
                        }
                      </p>
                    )}
                  </div>
                  {isPortalLoading ? (
                    <svg className="animate-spin h-4 w-4 shrink-0" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                    </svg>
                  ) : (
                    <svg className="shrink-0" width="20" height="20" fill="none" viewBox="0 0 20 20">
                      <path d="M4.167 10h11.666M10 4.167L15.833 10 10 15.833" stroke="#0D0C07" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  )}
                </div>
              </button>
            ) : (
              /* Free User - Premium Upsell */
              <div className="bg-white relative rounded-[12px] p-5 border-[0.5px] border-[#e2e1df]" style={{ boxShadow: '0px 1px 2px 0px rgba(35,33,22,0.02), 4px 12px 20px 0px rgba(83,81,69,0.12)' }}>
                <p className="text-[#518C03] font-light text-[20px] leading-[28px] mb-1">
                  {t('premium.onlyPrice', { price: prices.yearlyPerMonth })}
                </p>
                <p className="text-[#56554D] text-[14px] leading-[22px] mb-4">
                  {t('premium.subTitleMain')}
                </p>

                <div className="flex flex-col gap-2.5 mb-5">
                  {[t('premium.feature1'), t('premium.feature2'), t('premium.feature3'), t('premium.feature4'), t('premium.feature5')].map((feature, i) => (
                    <div key={i} className="flex gap-2 items-center">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                        <path d="M20 6L9 17L4 12" stroke="#0D0C07" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      <span className="text-[#0d0c07] text-[14px] leading-[22px]">{feature}</span>
                    </div>
                  ))}
                </div>

                <Link
                  to={`/premium?returnTo=${encodeURIComponent(returnToPath || `${location.pathname}${location.search || ''}`)}`}
                  className="w-full h-[44px] rounded-[32px] font-semibold text-[14px] text-white bg-[linear-gradient(200deg,_#090_0%,_#060_100%)] shadow-[0_2px_16px_0_rgba(0,0,0,0.12)_inset,_0_0_32px_0_rgba(255,255,255,0.4)_inset,_0_0px_0px_2px_rgba(0,102,0,0.4)] md:hover:opacity-90 transition-opacity flex items-center justify-center gap-1"
                >
                  {t('premium.becomePremiumButton', { days: prices.trialDays })}
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </Link>
              </div>
            )}
          </div>
          )}

          {/* Delete Account */}
          <p className="text-[14px] text-[#0d0c07] leading-[24px] mt-4">
            <Trans
              i18nKey="profileSettings.deleteText"
              ns="common"
              components={{
                a: (
                  <a
                    href="mailto:info@9meals.ai?subject=Delete%20My%209Meals%20Account"
                    className="font-medium text-[#d40b0b] underline underline-offset-2 md:hover:opacity-70 transition-opacity"
                  >placeholder</a>
                )
              }}
            />
          </p>

        </div>
      </div>

      {/* Footer */}
      <Footer />

      {/* Update Notification */}
      <UpdateNotification isVisible={showNotification} />

      {/* Language Drawer */}
      <LanguageDrawer
        isOpen={showLanguageDrawer}
        onClose={() => setShowLanguageDrawer(false)}
        onLanguageChange={handleLanguageChange}
      />
    </div>
  );
}

export default MyProfile;
